import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Button, Space, message, Divider, Dropdown, Tooltip, Checkbox, Tabs, Select, Popconfirm, Typography } from 'antd';
import AppLoading from '../components/AppLoading';
import {
  SaveOutlined, EyeOutlined, FullscreenExitOutlined, FullscreenOutlined, MenuUnfoldOutlined,
  ZoomInOutlined, ZoomOutOutlined, UndoOutlined, RedoOutlined, AlignLeftOutlined,
  AlignCenterOutlined, AlignRightOutlined, VerticalAlignTopOutlined, VerticalAlignBottomOutlined,
  ColumnHeightOutlined, ColumnWidthOutlined, DoubleLeftOutlined, DoubleRightOutlined, AppstoreOutlined,
  AimOutlined, CloseOutlined, CloudUploadOutlined,
} from '@ant-design/icons';
import { useParams, useSearchParams } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import DashboardCanvas, { CanvasView } from '../components/dashboard/DashboardCanvas';
import { useDeployStore } from '../stores/deployStore';
import ComponentPanel from '../components/dashboard/ComponentPanel';
import ConfigPanel from '../components/dashboard/ConfigPanel';
import LayerPanel2D from '../components/dashboard/LayerPanel2D';
import SceneDashboardView from '../components/dashboard/SceneDashboardView';

import RulerWrap from '../components/dashboard/Ruler';
import useDashboardStore from '../stores/dashboardStore';
import { useDashboardHistory } from '../stores/dashboardHistory';
import { useVisualFullscreen } from '../hooks/useVisualFullscreen';
import useEditorUiStore from '../stores/editorUiStore';
import { useDeviceLink3D } from '../hooks/useDeviceLink3D';
import { useEditLock } from '../hooks/useEditLock';
import { dashboardThumbnail } from '../utils/thumbnail';
import { useEmissiveArbiter } from '../hooks/useEmissiveArbiter';
import { useVisualBindingApply } from '../hooks/useVisualBindingApply';
import useSceneStore from '../stores/sceneStore';
import { fetchScene } from '../api/scenes';
import { fetchModels } from '../api/models';
import { useDeviceDataStore } from '../stores/deviceDataStore';
import { useSceneBindingStore, DEVICE_LINK_TYPE } from '../stores/sceneBindingStore';
import { broadcastFusion, subscribeFusion } from '../utils/fusionSync';
import AiDashboardPanel from '../components/dashboard/AiDashboardPanel';

export default function DashboardEditor(props: { pageId?: string; embedded?: boolean } = {}) {
  const { pageId: pageIdProp, embedded = false } = props;
  const { id: routeId } = useParams();
  const id = pageIdProp ?? routeId;
  // ④ 项目编辑锁：同一大屏仅 1 个编辑会话（嵌入式预览不申请锁）
  const { locked: editLocked, acquiring: lockAcquiring } = useEditLock('dashboard', embedded ? undefined : id);
  const [showPanels, setShowPanels] = useState(true);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<CanvasView>({ scaleX: 1, scaleY: 1 });
  const [fitSignal, setFitSignal] = useState(0);
  const [snapGrid, setSnapGrid] = useState(1);
  const [showGrid, setShowGrid] = useState(false);
  const [showRuler, setShowRuler] = useState(true);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [origin, setOrigin] = useState({ x: 0, y: 0 });
  const [viewLoaded, setViewLoaded] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const canvasAreaRef = useRef<HTMLDivElement>(null);
  const [show3DPreview, setShow3DPreview] = useState(false);
  const [searchParams] = useSearchParams();
  const show3DPreviewParam = searchParams.get('preview3d') === '1';
  const { isFullscreen, toggle: toggleFullscreen } = useVisualFullscreen();

  const {
    currentPage, components, selectedComponentId, dirty, saving,
    loadPage, savePage, patchPageConfig, patchPage, selectComponent, removeComponent, updateComponent,
  } = useDashboardStore();
  const history = useDashboardHistory();
  // Q3：项目级「关联3D场景」（顶部工具栏；不再放每个组件的属性配置里）
  const [sceneList, setSceneList] = useState<{ id: string; name: string; thumbnail?: string }[]>([]);
  useEffect(() => {
    import('../api/scenes')
      .then(({ fetchScenes }) => fetchScenes().then((list) => setSceneList(list || [])).catch(() => {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (id) {
      loadPage(id).then(() => setLoading(false)).catch(() => setLoading(false));
    }
  }, [id]);

  // 深链：?preview3d=1 打开即进入 3D 联动预览（页面加载完成且有关联场景时）
  useEffect(() => {
    if (!loading && currentPage && show3DPreviewParam) {
      open3DPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, currentPage?.id, show3DPreviewParam]);

  // 页面加载后，从config恢复view状态；如果没有保存过view，则触发fitScale
  useEffect(() => {
    if (currentPage && !viewLoaded) {
      const savedView = (currentPage.config as any)?.editorView;
      if (savedView && typeof savedView.scaleX === 'number') {
        setView({ scaleX: savedView.scaleX, scaleY: savedView.scaleY });
      } else {
        // 没有保存过view，触发自适应
        setFitSignal((s) => s + 1);
      }
      setViewLoaded(true);
    }
  }, [currentPage, viewLoaded]);

  useEffect(() => {
    const el = canvasAreaRef.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      setContainerSize({ width: rect.width, height: rect.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);

  // 全屏状态变化时触发一次适应，重置 view.scale，避免退出全屏后仍保持全屏的 fill 大 scale
  useEffect(() => {
    setFitSignal((s) => s + 1);
  }, [isFullscreen]);

  // 阶段3 反馈闭环（M3-2/M3-3）：大屏编辑器内直接可观测的 3D 联动——selectDevice → 高亮/飞行 + 状态条
  useDeviceLink3D();
  useEmissiveArbiter();
  // 阶段5：视觉规则绑定应用（联动预览同源实时）
  useVisualBindingApply();

  // 3D 联动预览：加载关联场景与模型（displayOnly 合成视图），点设备行/组件即高亮+飞行
  // 深链支持：/editor/dashboard/:id?preview3d=1 打开即进入联动预览（分享/演示直达）
  const open3DPreview = useCallback(async () => {
    const sceneId = currentPage?.sceneId;
    if (!sceneId) {
      message.info('当前大屏未关联 3D 场景（顶部工具栏 → 关联3D场景）');
      return;
    }
    try {
      const [scene, models] = await Promise.all([fetchScene(sceneId), fetchModels(sceneId)]);
      useSceneStore.getState().setScene(scene as any);
      useSceneStore.getState().setModels(models as any);
      // 2026-08-26｜联动预览自动漫游修复：setScene 不提取 waypoints，
      // 必须显式同步（否则 CameraPathPlayer 因 waypoints 为空静默不播，自动漫游点击无效果）
      useSceneStore.getState().setWaypoints((scene as any)?.config?.waypoints ?? []);
      // 2026-08-31｜第一人称自动游览路线同步（编辑器部署的浏览路线，演示页自动游览读取）
      useSceneStore.getState().setBrowseRoute((scene as any)?.config?.browseRoute ?? []);
      await useSceneBindingStore.getState().load(sceneId);
      // 绑定回填 models.deviceId（联动匹配用）
      const links = useSceneBindingStore.getState().bindings.filter((b) => b.visualType === DEVICE_LINK_TYPE && b.deviceId);
      if (links.length > 0) {
        useSceneStore.getState().setModels(
          useSceneStore.getState().models.map((m) => {
            const link = links.find((l) => l.modelNodeId === m.id);
            return link?.deviceId ? { ...m, deviceId: link.deviceId } : m;
          }),
        );
      }
      setShow3DPreview(true);
    } catch {
      message.error('加载关联 3D 场景失败');
    }
  }, [currentPage?.sceneId]);

  // Q3c：跨标签页同步——3D 编辑器侧关联/解除后，本页无需刷新即更新「关联3D场景」
  useEffect(() => {
    if (!id) return;
    return subscribeFusion((msg) => {
      if (msg.pageId !== id || msg.source === 'dashboard') return;
      const st = useDashboardStore.getState();
      if (st.currentPage && (st.currentPage.sceneId ?? null) !== msg.sceneId) {
        useDashboardStore.setState({
          currentPage: { ...st.currentPage, sceneId: msg.sceneId ?? undefined } as never,
        });
        message.info(msg.sceneId ? '已同步：3D 编辑器侧关联了本大屏' : '已同步：3D 编辑器侧解除了关联');
      }
    });
  }, [id]);

  const handleSave = async (): Promise<boolean> => {
    // 保存前将当前view写入config，只更新config不碰组件
    if (currentPage) {
      const config = { ...(currentPage.config || {}), editorView: { scaleX: view.scaleX, scaleY: view.scaleY } };
      // 缩略图：html2canvas 截取整个编辑区域快照，兜底背景色+名称占位图（存页面顶层 thumbnail 字段）
      const canvasHost = document.getElementById('dashboard-editor-root') as HTMLElement | null;
      const thumb = await dashboardThumbnail(canvasHost, currentPage.name || '大屏', (config as { backgroundColor?: string }).backgroundColor);
      if (thumb) {
        useDashboardStore.setState({ currentPage: { ...currentPage, thumbnail: thumb } as never });
        patchPageConfig(config);
      } else {
        patchPageConfig(config);
      }
    }
    const ok = await savePage();
    if (ok) {
      message.success('保存成功');
      // Q3c：广播关联状态（其他标签页的 3D 编辑器无需刷新即同步）
      if (id) {
        const sid = useDashboardStore.getState().currentPage?.sceneId ?? null;
        broadcastFusion({ type: 'page-scene-linked', pageId: id, sceneId: sid, source: 'dashboard' });
      }
    } else {
      message.error(useDashboardStore.getState().saveError || '保存失败');
    }
    return ok;
  };

  // 2026-10-05（用户反馈）：Ctrl+S 保存（此前会触发浏览器"保存网页"）；用 ref 避免按键监听频繁重绑
  const handleSaveRef = useRef<() => Promise<boolean>>(handleSave as () => Promise<boolean>);
  useEffect(() => { handleSaveRef.current = handleSave as () => Promise<boolean>; });

  /** B-07 双保存：返回 3D 前先保存大屏（有未保存修改时），保存失败不退出 */
  const handleBackTo3D = async () => {    if (!useDashboardStore.getState().dirty) {
      useEditorUiStore.getState().enterSceneEditor();
      return;
    }
    const ok = await handleSave();
    if (ok) {
      useEditorUiStore.getState().enterSceneEditor();
    } else {
      message.error('大屏保存失败，已留在编辑页，请重试');
    }
  };

  const selectedRect = useMemo(() => {
    if (!selectedComponentId) return null;
    const comp = components.find((c) => c.id === selectedComponentId);
    if (!comp) return null;
    return comp.position && comp.size ? { x: comp.position.x, y: comp.position.y, width: comp.size.width, height: comp.size.height } : null;
  }, [selectedComponentId, components]);

  const handleViewChange = useCallback((v: CanvasView) => setView(v), []);
  const zoomBy = useCallback((factor: number) => {
    setView((v) => ({ scaleX: Math.min(4, Math.max(0.2, v.scaleX * factor)), scaleY: Math.min(4, Math.max(0.2, v.scaleY * factor)) }));
  }, []);

  // ── 对齐选中组件（多选时组件间对齐，单选时对齐画布）──
  const alignComponent = useCallback((dir: string) => {
    const s = useDashboardStore.getState();
    // 2026-09-01｜多选（≥2）→ 组件间对齐（alignSelected）；单选 → 对齐画布
    if (s.selectedComponentIds.length >= 2) {
      const map: Record<string, 'left' | 'right' | 'top' | 'bottom' | 'hCenter' | 'vCenter'> = {
        left: 'left', right: 'right', top: 'top', bottom: 'bottom', centerX: 'hCenter', centerY: 'vCenter',
      };
      if (map[dir]) s.alignSelected(map[dir]);
      return;
    }
    const comp = s.components.find((c) => c.id === s.selectedComponentId);
    if (!comp) return;
    const pageW = s.currentPage?.config?.width ?? 1920;
    const pageH = s.currentPage?.config?.height ?? 1080;
    const { width, height } = comp.size;
    const pos = { ...comp.position };
    switch (dir) {
      case 'left': pos.x = 0; break;
      case 'centerX': pos.x = (pageW - width) / 2; break;
      case 'right': pos.x = pageW - width; break;
      case 'top': pos.y = 0; break;
      case 'centerY': pos.y = (pageH - height) / 2; break;
      case 'bottom': pos.y = pageH - height; break;
    }
    s.updateComponent(comp.id, { position: { x: Math.round(pos.x), y: Math.round(pos.y) } });
  }, []);

  // ── 图层快捷键（Ctrl+[ ]）与 撤销/重做/方向键/Delete ──
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const s = useDashboardStore.getState();
      const h = useDashboardHistory.getState();
      const ctrl = e.ctrlKey || e.metaKey;

      if (ctrl && e.key.toLowerCase() === 's') { e.preventDefault(); void handleSaveRef.current(); return; }
      if (ctrl && e.key.toLowerCase() === 'z') { e.preventDefault(); e.shiftKey ? h.redo() : h.undo(); return; }
      if (ctrl && e.key.toLowerCase() === 'y') { e.preventDefault(); h.redo(); return; }
      if (ctrl && e.key.toLowerCase() === 'r') { e.preventDefault(); setShowRuler((v) => !v); return; }
      if (ctrl && e.key.toLowerCase() === 'g') { e.preventDefault(); setShowGrid((v) => !v); return; }
      // 2026-09-01｜Ctrl+D 复制（多选时逐个复制）
      if (ctrl && e.key.toLowerCase() === 'd' && (s.selectedComponentIds.length > 0 || s.selectedComponentId)) {
        e.preventDefault();
        const ids = s.selectedComponentIds.length > 0 ? s.selectedComponentIds : [s.selectedComponentId as string];
        ids.forEach((id) => s.duplicateComponent(id));
        return;
      }

      if (ctrl && s.selectedComponentId) {
        if (e.key === ']') { e.preventDefault(); s.moveComponentZ(s.selectedComponentId, e.shiftKey ? 'top' : 'up'); return; }
        if (e.key === '[') { e.preventDefault(); s.moveComponentZ(s.selectedComponentId, e.shiftKey ? 'bottom' : 'down'); return; }
      }

      if (!s.selectedComponentId) return;
      const comp = s.components.find((c) => c.id === s.selectedComponentId);
      if (!comp) return;
      const locked = comp.config?.locked ?? comp.base?.locked ?? false;
      if (locked) return;
      const step = e.shiftKey ? 10 : 1;
      let dx = 0;
      let dy = 0;
      if (e.key === 'ArrowLeft') { dx = -step; e.preventDefault(); }
      else if (e.key === 'ArrowRight') { dx = step; e.preventDefault(); }
      else if (e.key === 'ArrowUp') { dy = -step; e.preventDefault(); }
      else if (e.key === 'ArrowDown') { dy = step; e.preventDefault(); }
      else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        // 2026-09-01｜多选删除
        if (s.selectedComponentIds.length > 1) {
          [...s.selectedComponentIds].forEach((id) => s.removeComponent(id));
        } else {
          s.removeComponent(comp.id);
        }
        return;
      }
      if (dx || dy) {
        s.updateComponent(comp.id, { position: { x: Math.round(comp.position.x + dx), y: Math.round(comp.position.y + dy) } });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [removeComponent, updateComponent]);

  // ── 设计分辨率切换（方案C：每页可配设计分辨率，发布时按真实大屏像素 contain 适配）──
  const RESOLUTIONS: Array<{ label: string; width: number; height: number }> = [
    { label: '1920 × 1080', width: 1920, height: 1080 },
    { label: '2560 × 1440', width: 2560, height: 1440 },
    { label: '3840 × 2160', width: 3840, height: 2160 },
    { label: '1366 × 768', width: 1366, height: 768 },
  ];
  const changeResolution = useCallback((val: string) => {
    if (!currentPage) return;
    const [w, h] = val.split('x').map(Number);
    const curW = currentPage?.config?.width ?? 1920;
    const curH = currentPage?.config?.height ?? 1080;
    if (!w || !h || (w === curW && h === curH)) return;
    patchPageConfig({ ...(currentPage.config || {}), width: w, height: h });
    setFitSignal((s) => s + 1);
    message.info(`设计分辨率已切换为 ${w} × ${h}，请点击保存生效`);
  }, [currentPage, patchPageConfig]);

  if (loading) {
    return <AppLoading />;
  }

  const pageWidth = currentPage?.config?.width ?? 1920;
  const pageHeight = currentPage?.config?.height ?? 1080;

  const RULER_SIZE = 22;
  const availW = containerSize.width - (showRuler ? RULER_SIZE : 0);
  const availH = containerSize.height - (showRuler ? RULER_SIZE : 0);
  // 画布完全填满容器：scaleX=availW/pageWidth, scaleY=availH/pageHeight（独立拉伸），
  // 组件贴边、无马赛克空隙、不越界。全屏/非全屏/面板折叠均一致。
  const scaleX = availW > 0 && pageWidth > 0 ? availW / pageWidth : view.scaleX;
  const scaleY = availH > 0 && pageHeight > 0 ? availH / pageHeight : view.scaleY;
  const safeView: CanvasView = { scaleX, scaleY };

  const alignItems = [
    { key: 'left', label: '左对齐', icon: <AlignLeftOutlined /> },
    { key: 'centerX', label: '水平居中', icon: <AlignCenterOutlined /> },
    { key: 'right', label: '右对齐', icon: <AlignRightOutlined /> },
    { key: 'top', label: '顶对齐', icon: <VerticalAlignTopOutlined /> },
    { key: 'centerY', label: '垂直居中', icon: <ColumnHeightOutlined /> },
    { key: 'bottom', label: '底对齐', icon: <VerticalAlignBottomOutlined /> },
  ];

  const rightTabs = [
    {
      key: 'props',
      label: '属性配置',
      // 2026-10-05（用户反馈）：数据绑定并入 ConfigPanel「交互联动」内一份，不再在下方重复
      children: <ConfigPanel />,
    },
    { key: 'layers', label: '图层', children: <LayerPanel2D /> },
  ];

  return (
    <DndProvider backend={HTML5Backend}>
      {/* ④ 项目编辑锁：被他人占用时只读提示 */}
      {!editLocked && !lockAcquiring && (
        <div style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, background: '#722ed1', color: '#fff', padding: '4px 16px', borderRadius: 4, fontSize: 13 }}>
          该项目正在被其他用户编辑，当前为只读模式
        </div>
      )}
      <div id="dashboard-editor-root" style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#1e1e2e' }}>
        {/* ── 顶部工具栏 ── */}
        {showPanels && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '4px 12px', background: '#1a1a2e', borderBottom: '1px solid #333', flexShrink: 0,
          }}>
            <Space size={4} wrap>
              {embedded && (
                <Button size="small" type="text" icon={<DoubleRightOutlined />} onClick={handleBackTo3D}>
                  返回3D编辑
                </Button>
              )}
              <Button size="small" type="text" icon={<MenuUnfoldOutlined />} onClick={() => setShowPanels(!showPanels)} />
              <Button size="small" type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
                {dirty ? '保存*' : '保存'}
              </Button>
              <Button size="small" icon={<EyeOutlined />} onClick={() => window.open(`/preview/dashboard/${id}`, '_blank')}>预览</Button>
              {/* DW-P0-8：大屏发布入口（项目部署对话框） */}
              <Button
                size="small"
                icon={<CloudUploadOutlined />}
                data-testid="deploy-open"
                onClick={() => useDeployStore.getState().openModal({ kind: 'dashboard', pageId: id, name: currentPage?.name })}
              >
                发布
              </Button>
              <AiDashboardPanel />
              <Button
                size="small"
                icon={<AimOutlined />}
                disabled={!currentPage?.sceneId}
                onClick={open3DPreview}
              >
                3D联动预览
              </Button>
              <Select
                size="small"
                data-testid="assoc-scene-select"
                style={{ width: 170 }}
                placeholder="关联3D场景"
                allowClear
                value={currentPage?.sceneId || undefined}
                onChange={(val) => {
                  patchPage({ sceneId: val || undefined });
                  if (id) broadcastFusion({ type: 'page-scene-linked', pageId: id, sceneId: val || null, source: 'dashboard' });
                  message.info(val ? '已关联 3D 场景（点击保存生效）' : '已取消关联（点击保存生效）');
                }}
                options={sceneList.map((s) => ({ value: s.id, label: s.name }))}
              />
              {/* 绑定场景缩略图（仅展示：有缩略图才显示；无缩略图不显示；不可点击） */}
              {currentPage?.sceneId && (() => {
                const sc = sceneList.find((s) => s.id === currentPage.sceneId);
                if (!sc?.thumbnail) return null;
                return (
                  <div
                    data-testid="assoc-scene-preview"
                    title={sc.name || '关联的场景'}
                    style={{
                      width: 64, height: 36, borderRadius: 4, overflow: 'hidden',
                      border: '1px solid #30475e', background: '#12121c', flexShrink: 0,
                    }}
                  >
                    <img src={sc.thumbnail} alt={sc.name || '场景'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                );
              })()}
              {isFullscreen ? (
                <Button size="small" icon={<FullscreenExitOutlined />} onClick={toggleFullscreen}>退出全屏</Button>
              ) : (
                <Button size="small" icon={<FullscreenOutlined />} onClick={toggleFullscreen}>全屏设计</Button>
              )}
              <Divider orientation="vertical" />
              <Tooltip title="撤销 (Ctrl+Z)">
                <Button size="small" icon={<UndoOutlined />} disabled={history.past.length === 0} onClick={() => history.undo()} />
              </Tooltip>
              <Tooltip title="重做 (Ctrl+Y)">
                <Button size="small" icon={<RedoOutlined />} disabled={history.future.length === 0} onClick={() => history.redo()} />
              </Tooltip>
              <Dropdown
                menu={{ items: alignItems.map((a) => ({ key: a.key, label: a.label, icon: a.icon, onClick: () => alignComponent(a.key) })) }}
                placement="bottom"
              >
                <Button size="small" icon={<ColumnWidthOutlined />}>对齐</Button>
              </Dropdown>
              <Divider orientation="vertical" />
              {/* 网格 / 吸附 / 标尺 聚合一组 */}
              <Checkbox checked={showGrid} onChange={(e) => setShowGrid(e.target.checked)} style={{ fontSize: 12, color: '#888' }}>
                网格 (Ctrl+G)
              </Checkbox>
              <Button size="small" onClick={() => { setSnapGrid(snapGrid === 1 ? 10 : 1); }}>
                吸附 {snapGrid}px
              </Button>
              <Checkbox checked={showRuler} onChange={(e) => setShowRuler(e.target.checked)} style={{ fontSize: 12, color: '#888' }}>
                标尺 (Ctrl+R)
              </Checkbox>
              <Divider orientation="vertical" />
              <Button size="small" icon={<ZoomOutOutlined />} onClick={() => zoomBy(0.8)} />
              <span style={{ fontSize: 11, color: '#888', width: 44, textAlign: 'center' }}>{Math.round(((scaleX + scaleY) / 2) * 100)}%</span>
              <Button size="small" icon={<ZoomInOutlined />} onClick={() => zoomBy(1.25)} />
              <Button size="small" onClick={() => setFitSignal((s) => s + 1)}>适应</Button>
              {dirty && <span style={{ fontSize: 11, color: '#faad14' }}>有未保存的修改</span>}
            </Space>
            <Space>
              <Select
                size="small"
                value={`${pageWidth}x${pageHeight}`}
                onChange={changeResolution}
                options={RESOLUTIONS.map((r) => ({ value: `${r.width}x${r.height}`, label: r.label }))}
                style={{ width: 110 }}
              />
              <span style={{ fontSize: 11, color: '#888' }}>Ctrl+滚轮缩放 · 拖拽空白平移</span>
            </Space>
          </div>
        )}

        {/* ── 主内容区 ── */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* 左侧：组件库（可折叠） */}
          {showPanels && (
            leftCollapsed ? (
              <div style={{ width: 40, background: '#1a1a2e', borderRight: '1px solid #333', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 8, flexShrink: 0 }}>
                <Tooltip title="展开组件库"><Button size="small" type="text" icon={<DoubleRightOutlined />} onClick={() => setLeftCollapsed(false)} /></Tooltip>
                <AppstoreOutlined style={{ color: '#888', fontSize: 18, marginTop: 12 }} />
              </div>
            ) : (
              <div style={{ width: 200, background: '#1a1a2e', borderRight: '1px solid #333', overflow: 'auto', flexShrink: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 6px 0' }}>
                  <Button size="small" type="text" icon={<DoubleLeftOutlined />} onClick={() => setLeftCollapsed(true)} title="收起" />
                </div>
                <ComponentPanel />
              </div>
            )
          )}

          {/* 中间：画布 */}
          <div ref={canvasAreaRef} style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative', background: 'repeating-conic-gradient(#22222e 0% 25%, #1a1a26 0% 50%) 0 0 / 24px 24px' }}>
            {showRuler ? (
              <RulerWrap
                width={pageWidth}
                height={pageHeight}
                scaleX={scaleX}
                scaleY={scaleY}
                originX={origin.x}
                originY={origin.y}
                onOriginChange={(x, y) => setOrigin({ x, y })}
                selectedRect={selectedRect}
              >
                <DashboardCanvas view={safeView} onViewChange={handleViewChange} fitSignal={fitSignal} snap={snapGrid} showGrid={showGrid} />
              </RulerWrap>
            ) : (
              <DashboardCanvas view={safeView} onViewChange={handleViewChange} fitSignal={fitSignal} snap={snapGrid} showGrid={showGrid} />
            )}
          </div>

          {/* 右侧：属性/图层 Tab（可折叠） */}
          {showPanels && (
            rightCollapsed ? (
              <div style={{ width: 40, background: '#1a1a2e', borderLeft: '1px solid #333', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 8, flexShrink: 0 }}>
                <Tooltip title="展开属性面板"><Button size="small" type="text" icon={<DoubleLeftOutlined />} onClick={() => setRightCollapsed(false)} /></Tooltip>
                <AppstoreOutlined style={{ color: '#888', fontSize: 18, marginTop: 12 }} />
              </div>
            ) : (
              <div style={{ width: 280, background: '#1a1a2e', borderLeft: '1px solid #333', overflow: 'auto', flexShrink: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 6px 0' }}>
                  <Button size="small" type="text" icon={<DoubleRightOutlined />} onClick={() => setRightCollapsed(true)} title="收起" />
                </div>
                <Tabs size="small" items={rightTabs} />
              </div>
            )
          )}
        </div>
      </div>

      {/* 阶段3｜M3-2/M3-3 联动预览：3D 场景 + 2D 大屏合成视图（点行/点组件即高亮+飞行，状态条可观测） */}
      {show3DPreview && currentPage?.sceneId && (
        <div data-testid="link-preview" style={{ position: 'absolute', inset: 0, zIndex: 300, background: '#000' }}>
          <SceneDashboardView
            sceneId={currentPage.sceneId}
            components={components}
            pageWidth={pageWidth}
            pageHeight={pageHeight}
            backgroundColor={currentPage.config?.backgroundColor}
            backgroundImage={currentPage.config?.backgroundImage}
            show3D
            lowQuality
          />
          <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 310, display: 'flex', alignItems: 'center', gap: 10 }}>
            <LinkStatusStrip />
          </div>
          <Button
            size="small"
            style={{ position: 'absolute', top: 10, right: 10, zIndex: 310 }}
            icon={<CloseOutlined />}
            onClick={() => setShow3DPreview(false)}
          >
            退出联动预览
          </Button>
        </div>
      )}
    </DndProvider>
  );
}

/** 3D 联动状态条：显示当前 selectDevice 选中设备与其绑定的 3D 模型（2D 驱动 3D 可观测反馈） */
function LinkStatusStrip() {
  const selectedDeviceId = useDeviceDataStore((s) => s.selectedDeviceId);
  const models = useSceneStore((s) => s.models);
  const selectedModel = selectedDeviceId ? models.find((m) => m.deviceId === selectedDeviceId) : null;
  const Text = Typography.Text;
  const bg = selectedModel ? 'rgba(22,119,255,0.85)' : 'rgba(30,30,46,0.85)';
  return (
    <div style={{ background: bg, color: '#fff', borderRadius: 6, padding: '4px 10px', fontSize: 12 }}>
      {selectedDeviceId ? (
        <>
          联动设备: <b>{selectedDeviceId}</b>
          {' → '}
          {selectedModel ? (
            <b style={{ color: '#b7e3ff' }}>3D模型「{selectedModel.name}」已高亮选中（相机定位）</b>
          ) : (
            <span style={{ color: '#ffd591' }}>3D 未找到绑定该设备的模型（模型属性「设备联动」可绑定）</span>
          )}
        </>
      ) : (
        <span>点击「设备列表」行或带联动动作的组件，3D 模型将高亮+飞行定位</span>
      )}
    </div>
  );
}
